<template>
  <div class="card">
    <template v-if="null !== vetlla && -1 !== vetlla.Id">
      <h2>QR per la vetlla de '{{ nombreDifunto }}'</h2>
      <qrcode-vue
        :value="qrVal"
        :level="qrLevel"
        :render-as="qrRenderAs"
        :class="qrClassName"
        :size="300"
      />
      <div class="buttons">
        <button class="follow-button" @click="followQr">Ves a l'enllaç</button>
        <button class="download-button" @click="downloadQr">
          Descarrega QR
        </button>
      </div>
    </template>
    <template v-else>
      <h2>No s'ha trobat cap vetlla a la sala '{{ salaVetlla }}'</h2>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, PropType } from "vue";
import { useRouter } from "vue-router";

import QrcodeVue, { Level, RenderAs } from "qrcode.vue";
import axios from "axios";

import CustomSwal from "../custom-swal.ts";
import useErpApiClient from "../erp-api-client";
import { getUploadPath } from "../helpers";
import { Vetlla, Sala } from "../types/vetlla.ts";

export default defineComponent({
  components: {
    QrcodeVue,
  },
  props: {
    id: {
      required: true,
      type: Number,
    },
    qrType: {
      required: true,
      type: String as PropType<"vetlla" | "sala">,
    },
  },
  setup(props) {
    const erpClient = useErpApiClient();
    const router = useRouter();

    const baseUrl = import.meta.env.VITE_HOST;
    const qrVal = ref(baseUrl);
    const qrLevel = ref<Level>("M");
    const qrRenderAs = ref<RenderAs>("canvas");
    const qrClassName = ref("qr-vetlla-canvas");

    const vetlla = ref<null | Vetlla>(null);
    const sala = ref<null | Sala>(null);

    const nombreDifunto = ref<null | string>(null);
    const salaVetlla = ref<null | string>(null);

    const downloadQr = () => {
      var link = document.createElement("a");
      link.download = `qr-${vetlla.value?.NombreDifunto}.png`;
      link.href = (
        document.getElementsByClassName(
          qrClassName.value,
        )[0] as HTMLCanvasElement
      ).toDataURL();
      link.click();
    };

    const followQr = () => {
      if ("vetlla" === props.qrType) {
        if (null !== vetlla.value) {
          router.push(getUploadPath(props.qrType, vetlla.value.Id));
        } else {
          console.error("Vetlla es NULL");
        }
      } else {
        if (null !== sala.value) {
          router.push(getUploadPath(props.qrType, sala.value.Id));
        } else {
          console.error("Sala es NULL");
        }
      }
    };

    onMounted(() => {
      if ("vetlla" === props.qrType) {
        axios.get(`/api/vetlla/read?id=${props.id}`).then((response) => {
          vetlla.value = response.data as Vetlla;
          qrVal.value =
            qrVal.value + getUploadPath(props.qrType, vetlla.value.Id);

          nombreDifunto.value = vetlla.value.NombreDifunto;
          salaVetlla.value = "";
        });
      } else {
        let salas: Sala[] = [];
        erpClient
          .request(() => axios.get("/api/sala/index"))
          .then((response) => {
            salas = response.data;
            const salaPresentation = salas.find((s) => props.id == s.Id);

            if (undefined === salaPresentation) {
              CustomSwal.fire({
                icon: "error",
                title: "S'ha produït un error",
                text: `No s'ha pogut trobar la sala amb id '${props.id}'`,
              });
            } else {
              sala.value = salaPresentation;
              salaVetlla.value = sala.value.Nombre;
              axios
                .get(`/api/vetlla/read?id=${salaPresentation.IdVetlla}`)
                .then((response) => {
                  vetlla.value = response.data as Vetlla;
                  nombreDifunto.value = vetlla.value.NombreDifunto;

                  if (null !== sala.value) {
                    qrVal.value =
                      qrVal.value + getUploadPath(props.qrType, sala.value?.Id);
                  } else {
                    console.error("Sala es NULL");
                  }
                });
            }
          });
      }
    });

    return {
      qrVal,
      qrLevel,
      qrRenderAs,
      qrClassName,
      downloadQr,
      followQr,
      nombreDifunto,
      salaVetlla,
      vetlla,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  gap: 1em;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;

  width: 80%;
}

.buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2em;
}

.follow-button {
  @include button($accessory-color);
}

.download-button {
  @include button($primary-color);
}
</style>
